import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase, getUserProfile, signOut } from '../lib/supabase';
import { UserChip } from './UserChip';
import { MobileAccountSheet } from './MobileAccountSheet';
import { stopActAsShop, getActAsShopId } from './AppShell';
import { User, ArrowLeft, LogOut } from 'lucide-react';
import '../styles/instacut.css';

/**
 * AccountShell — dedicated shell for the personal-user settings area.
 *
 * We deliberately keep this separate from AppShell / AdminShell so the
 * sidebar shows only "Account" nav items (My account today; things like
 * Notifications, Sessions, Two-factor can slot in later without polluting
 * the shop or platform menus).
 *
 * Exposes an AccountShellContext so pages can read the loaded profile
 * without refetching, matching the pattern in AppShell / AdminShell.
 */
export const AccountShellContext = React.createContext({ profile: null });
export const useAccountShell = () => React.useContext(AccountShellContext);

const NAV = [
  { to: '/account', label: 'My account', icon: User },
];

export const AccountShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [sessionEmail, setSessionEmail] = useState('');
  const [checking, setChecking] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!supabase) { navigate('/signin'); return; }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/signin'); return; }
      const { data } = await getUserProfile(session.user.id);
      if (!mounted) return;
      setProfile(data);
      setSessionEmail(session.user.email || '');
      setChecking(false);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    stopActAsShop();
    await signOut();
    navigate('/signin');
  };

  // Route the "Back" link based on where the user should return to.
  // Master admins go to the platform console; everyone else goes to
  // their shop dashboard. If a master admin was acting-as-shop when
  // they opened My account, keep that impersonation intact so the
  // Back link takes them straight to the shop dashboard they were on.
  const isMaster = profile?.role === 'master_admin';
  const wasImpersonating = !!getActAsShopId();
  const backHref = isMaster && !wasImpersonating ? '/admin/shops' : '/dashboard';
  const backLabel = isMaster && !wasImpersonating ? 'Back to Platform' : 'Back to Shop';

  if (checking) {
    return (
      <div className="app-root" data-testid="accountshell-loading">
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--mute)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div className="app-root">
      <div className="app">
        <aside className="side" data-testid="account-sidebar">
          <Link to={backHref} className="brandrow">
            <img className="mark" src="/logos/instacut-mark-26.svg" alt="" width="26" height="26" />
            Instacut
          </Link>

          {/* Persistent way back to whichever shell the user came from. */}
          <Link
            to={backHref}
            className="nav back-to-platform"
            data-testid="nav-account-back"
            style={{
              margin: '4px 8px 10px',
              background: '#0e0f13',
              color: '#fff',
              border: '1px solid #0e0f13',
              borderRadius: 9,
              justifyContent: 'flex-start',
              padding: '9px 12px',
              fontWeight: 600,
            }}
          >
            <ArrowLeft size={15} />
            {backLabel}
          </Link>

          <div className="navg">Account</div>
          {NAV.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`nav ${active ? 'on' : ''}`}
                data-testid={`account-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="i" size={17} />
                {item.label}
              </Link>
            );
          })}

          <UserChip profile={profile} email={sessionEmail} />
          <button className="signout" onClick={handleSignOut} data-testid="account-signout">
            <LogOut size={15} /> Sign out
          </button>

          <MobileAccountSheet
            profile={profile}
            email={sessionEmail}
            onSignOut={handleSignOut}
            active={mobileMenuOpen}
            onOpen={() => setMobileMenuOpen(true)}
            onClose={() => setMobileMenuOpen(false)}
            testIdPrefix="account-mobile-account"
          />
        </aside>

        <AccountShellContext.Provider value={{ profile, backHref, backLabel }}>
          {children}
        </AccountShellContext.Provider>
      </div>
    </div>
  );
};
